import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import ctl from '@netlify/classnames-template-literals'
import { ReactNode } from 'react'
import { FilterBox, FilterBoxTheme } from './filter-box'
import { CheckmarkIcon, ChevronIcon, FilterIcon } from './filter-icons'
import type { SelectFilterOption } from './select-filter'

export type SelectMultipleFilterProps<T extends string> = {
	options: ReadonlyArray<SelectFilterOption<T>>
	values: ReadonlyArray<T>
	onChange: (values: ReadonlyArray<T>) => void
	placeholder?: string
	/**
	 * If true (default), the button will show the count of selected options.
	 * Provide a custom renderer via `renderSelectedLabel` to override.
	 */
	showSelectedCount?: boolean
	/**
	 * Optional custom renderer for the button label when there are selections.
	 */
	renderSelectedLabel?: (
		selectedOptions: ReadonlyArray<SelectFilterOption<T>>,
	) => ReactNode
	theme?: FilterBoxTheme
}

export function SelectMultipleFilter<T extends string>({
	options,
	values,
	onChange,
	placeholder,
	showSelectedCount = true,
	renderSelectedLabel,
	theme,
}: SelectMultipleFilterProps<T>) {
	const selectedOptions = options.filter((option) => values.includes(option.id))

	let buttonLabel: ReactNode
	if (selectedOptions.length === 0) {
		buttonLabel = placeholder
	} else if (renderSelectedLabel) {
		buttonLabel = renderSelectedLabel(selectedOptions)
	} else if (showSelectedCount) {
		buttonLabel = `${selectedOptions.length} selected`
	} else {
		// Fallback: show first label and a "+N" for the rest
		const [first, ...rest] = selectedOptions
		buttonLabel = (
			<span>
				{first.label}
				{rest.length > 0 ? ` +${rest.length}` : null}
			</span>
		)
	}

	return (
		<FilterBox icon={<FilterIcon />} theme={theme}>
			<Listbox value={values} onChange={onChange} multiple>
				<ListboxButton
					className={ctl(`
						rounded-md p-2 pr-8 w-full text-left
						focus:outline-none
						group relative
						text-sm
					`)}
				>
					{buttonLabel}
					<div className="absolute right-3 top-1/2 -translate-y-1/2">
						<ChevronIcon />
					</div>
				</ListboxButton>
				<ListboxOptions
					anchor="bottom"
					className="bg-white min-w-[calc(var(--button-width)+8px)] [--anchor-gap:8px] ring-1 py-1 ring-grey-100 shadow-md rounded text-sm text-grey-600"
				>
					{options.map((option) => (
						<ListboxOption
							key={option.id}
							value={option.id}
							className={ctl(`
								relative
								group
								data-[selected]:bg-blue-50
								data-[selected]:text-blue-600
								cursor-pointer data-focus:bg-blue-100 mx-1 px-2 py-1 rounded
							`)}
						>
							{option.label}
							<div className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-500 opacity-0 group-data-[selected]:opacity-100">
								<CheckmarkIcon />
							</div>
						</ListboxOption>
					))}
				</ListboxOptions>
			</Listbox>
		</FilterBox>
	)
}
